"use client";

import { LoadingSkeleton } from "@/components/loading-skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { ProductCard } from "@/components/blocks/ProductCard";
import {
  getProductImages,
  isPopulatedShop,
  type CustomerShopSummary,
} from "@/features/shop/types/customer-shop";
import { formatProductUnitLabel } from "@/features/shop/types";
import { formatShopLocation } from "@/features/shop/shop-location";
import { formatPublicCategoryLabel } from "@/features/public-site/public-site.utils";
import type { ProductSectionProps } from "@/features/shop/components/shop-page.types";
import { useCartStore } from "@/features/shop/cart/cart.store";
import { useSetCustomerCartItemQuantity } from "@/features/shop/customer/customer-shop.mutations";

export function ProductSection({
  title,
  description,
  products,
  quantities,
  onQuantityChange,
  onAddToCart,
  isPending,
  loading,
  error,
  emptyMessage,
  gridClassName,
  cardCount = 8,
  showQuantity = true,
}: ProductSectionProps) {
  const cartQuantities = useCartStore((state) => state.quantities);
  const setCartItemQuantity = useSetCustomerCartItemQuantity();
  return (
    <Card className="border-border bg-card shadow-sm">
      <CardHeader className="space-y-1">
        <CardTitle className="text-xl">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <LoadingSkeleton
            variant="cards"
            count={cardCount}
            gridClassName={gridClassName}
            itemClassName="border-border bg-card"
            showMedia
          />
        ) : error ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error.message}
          </div>
        ) : products.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          <div className={gridClassName ?? "grid gap-4 md:grid-cols-2 xl:grid-cols-4"}>
            {products.map((product) => {
              const shop = isPopulatedShop(product.shopId)
                ? (product.shopId as CustomerShopSummary)
                : null;

              return (
                <ProductCard
                  key={product._id}
                  variant="storefront"
                  name={product.name}
                  description={product.description || "Fresh produce."}
                  category={formatPublicCategoryLabel(product.category)}
                  extraBadge={{
                    label: product.quantity > 0 ? "New" : "Sold Out",
                    tone: "destructive",
                  }}
                  imageUrl={getProductImages(product)[0]}
                  priceLabel={`KES ${product.price}`}
                  unitLabel={`${product.quantity} ${formatProductUnitLabel(product.unit)}`}
                  locationLabel={
                    shop
                      ? `${shop.name} · ${formatShopLocation(shop)}`
                      : "Local farm shop"
                  }
                  quantity={quantities[product._id] ?? 1}
                  maxQuantity={product.quantity}
                  showQuantity={showQuantity}
                  onQuantityChange={(value) => onQuantityChange(product._id, value)}
                  onAddToCart={() => onAddToCart(product)}
                  isPending={isPending}
                  cartQuantity={cartQuantities[product._id] ?? 0}
                  onCartQuantityChange={(nextQuantity) =>
                    setCartItemQuantity.mutate({
                      productId: product._id,
                      quantity: nextQuantity,
                    })
                  }
                  isCartUpdating={
                    setCartItemQuantity.isPending &&
                    setCartItemQuantity.variables?.productId === product._id
                  }
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
